import { PhpArray, PhpFloat, empty, isset, intval, strval, trim, strtoupper, phpCopy, json_encode,
    array_values, in_array, type PhpInt } from '../../../../php-compat';
import type { PhpRef, PortEnv } from '../../../../port-contract';
import { phpAdd } from '../../../../compile/php-integer-arithmetic';
import {SKILLS} from '../../catalog-constants';
import { array, get, isArray, list, put, record , values } from '../../array-path';

export interface ComputeBaseDeps {
    _ft_default_provider_carriers(host: unknown): unknown;
    return_int_score_item_layer_type_name(host: unknown, layer: string, base: number): unknown;
    layer_entries(host: unknown, layer: string): unknown;
    layer_value(host: unknown, layer: string): unknown;
    item_arr_scalar_layer_slot(value: unknown): unknown;
    check_uuid(value: string): boolean;
    layer_list_entry_uuid(value: unknown): string;
    query_full_item_arr(id: string): unknown;
    layer_has(host: unknown, layer: string): boolean;
    build_enchantment_template_arr(id: string, full: boolean): unknown;
    caster_skills(host: unknown): unknown;
    ft_hydrate_skill_entry(entry: unknown, variable: string): unknown;
}
/** Function-local PHP statics have request lifetime but are not PHP globals. */
export type ComputeBaseEnv = PortEnv<ComputeBaseDeps> & { functionStatics?: Record<string, unknown> };

/** PHP: ft_compute_source_row (includes/functions/janus/core/compute/_base.php:31) */
export function ftComputeSourceRow(category: string, label: string, value: PhpInt, href_id: string,
    kind: string, value_brace: PhpInt | null = null): PhpArray {
    if (trim(label) === '') label = 'Unknown source';
    const row = record({ category, label, value, href_id, kind });
    if (value_brace !== null) row.set('value_brace', value_brace);
    return row;
}

/** PHP: ft_compute_collect_carrier_sources (includes/functions/janus/core/compute/_base.php:70) */
export function ftComputeCollectCarrierSources(item_arr: unknown, layer_name: string,
    env: PortEnv<Pick<ComputeBaseDeps, '_ft_default_provider_carriers' | 'return_int_score_item_layer_type_name'>>): PhpArray {
    const rows = new PhpArray();
    for (const carrier of values(env.deps._ft_default_provider_carriers(item_arr))) {
        const layers = get(carrier, 'item_layer_types');
        if (!isArray(carrier) || empty(layers) || !isArray(layers) || !array(layers).has(layer_name)) continue;
        const contribution = intval(env.deps.return_int_score_item_layer_type_name(carrier, layer_name, 0));
        if (contribution === 0) continue;
        const name = get(carrier, 'items', 'item_name') ?? '', id = get(carrier, 'items', 'item_id') ?? '';
        const type = get(carrier, 'items', 'item_type') ?? '';
        rows.append(ftComputeSourceRow(type !== '' ? strval(type) : 'carrier', strval(name), contribution,
            strval(id), type !== '' ? strval(type) : 'carrier'));
    }
    return rows;
}

/** PHP: ft_compute_collect_host_source (includes/functions/janus/core/compute/_base.php:113) */
export function ftComputeCollectHostSource(item_arr: unknown, layer_name: string,
    env: PortEnv<Pick<ComputeBaseDeps, 'return_int_score_item_layer_type_name'>>): PhpArray | null {
    const authored = get(item_arr, 'item_layer_types', layer_name);
    if (!isset(authored)) return null;
    const host = record({ item_layer_types: record({ [layer_name]: authored }) });
    const value = intval(env.deps.return_int_score_item_layer_type_name(host, layer_name, 0));
    if (value === 0) return null;
    return ftComputeSourceRow('host', strval(get(item_arr, 'items', 'item_name') ?? 'Host'), value,
        strval(get(item_arr, 'items', 'item_id') ?? ''), 'host');
}

/** PHP: ft_equipped_row_authored_layer_fragments (includes/functions/janus/core/compute/_base.php:197) */
export function ftEquippedRowAuthoredLayerFragments(row: unknown, layer: string,
    browser_style = false, env: ComputeBaseEnv): PhpArray {
    let frags = array_values(array(env.deps.layer_entries(row, layer)));
    if (frags.size === 0) return frags;
    const template = strtoupper(strval(env.deps.item_arr_scalar_layer_slot(env.deps.layer_value(row, 'enchantment_template_applied') ?? '') ?? ''));
    if (!env.deps.check_uuid(template)) return frags;
    let copies = new PhpArray();
    if (browser_style) {
        for (const entry of array(env.deps.layer_entries(row, 'passive_ability')).values()) {
            const pid = env.deps.layer_list_entry_uuid(entry);
            const host = env.deps.check_uuid(strval(pid)) ? env.deps.query_full_item_arr(strval(pid)) : null;
            if (!isArray(host) || env.deps.layer_has(host, 'requires_equipped_item')) continue;
            for (const fragment of array(env.deps.layer_entries(host, layer)).values()) copies.append(phpCopy(fragment));
        }
    } else {
        // Preserve the function-local cache without creating observable PHP globals.
        const state_key = 'ft_equipped_row_authored_layer_fragments::$template_frags';
        const statics = env.functionStatics ??= {};
        const cache = array(statics[state_key]);
        statics[state_key] = cache;
        const key = template + '|' + layer;
        if (!cache.has(key)) {
            const fragments = new PhpArray(), data = env.deps.build_enchantment_template_arr(template, true);
            for (const nested of array(get(data, 'nested_abilities', 'passive_ability')).values()) {
                const host = get(nested, 'item_arr') ?? null;
                if (!isArray(host) || env.deps.layer_has(host, 'requires_equipped_item')) continue;
                for (const fragment of array(env.deps.layer_entries(host, layer)).values()) fragments.append(phpCopy(fragment));
            }
            cache.set(key, fragments);
        }
        copies = array(cache.get(key));
    }
    for (const copy of copies.values()) {
        const needle = json_encode(copy);
        for (const [key, fragment] of frags) if (json_encode(fragment) === needle) { frags.delete(key); break; }
    }
    frags = array_values(frags);
    return frags;
}

/** PHP: ft_compute_breakdown_from_sources (includes/functions/janus/core/compute/_base.php:472) */
export function ftComputeBreakdownFromSources(sources: unknown): PhpArray {
    const breakdown = new PhpArray();
    for (const row of array(sources).values()) {
        const category = strval(get(row, 'category') ?? get(row, 'kind') ?? '');
        const before = breakdown.get(category) ?? 0;
        const total = phpAdd(before instanceof PhpFloat ? before.value : BigInt(before as PhpInt),
            BigInt(intval(get(row, 'value') ?? 0)));
        breakdown.set(category, typeof total === 'bigint' ? total : new PhpFloat(total));
    }
    return breakdown;
}

/** PHP: ft_compute_read_total (includes/functions/janus/core/compute/_base.php:586) */
export function ftComputeReadTotal(item_arr: unknown, cluster: string, name: string,
    fallback: PhpInt | string | boolean = 0): unknown {
    if (item_arr === null) return fallback;
    const slot = get(item_arr, 'computed', 'creature', cluster, name) ?? null;
    if (slot === null) return fallback;
    return isArray(slot) ? get(slot, 'total') ?? fallback : slot;
}

/** PHP: caster_skills_ref (includes/functions/janus/core/compute/_base.php:626) */
export function casterSkillsRef(arr: PhpRef<unknown>): PhpArray {
    arr.value = phpCopy(array(arr.value));
    if (!isArray(get(arr.value, 'computed', 'creature', 'skills')))
        put(array(arr.value), ['computed', 'creature', 'skills'], new PhpArray());
    return array(get(arr.value, 'computed', 'creature', 'skills'));
}

/** PHP: ft_skill_registry_by_var (includes/functions/janus/core/compute/_base.php:654) */
export function ftSkillRegistryByVar(env: ComputeBaseEnv): PhpArray {
    const key='ft_skill_registry_by_var::$by_var',statics=env.functionStatics;
    if (isset(statics[key])) return phpCopy(array(statics[key]));
    const registry = new PhpArray();
    for (const skill of array(SKILLS).values()) {
        const variable = strval(get(skill, 'variable_name') ?? '');
        if (variable !== '') registry.set(variable, record({ item_id: strval(get(skill, 'item_id') ?? ''),
            item_name: strval(get(skill, 'item_name') ?? ''), item_type: strval(get(skill, 'item_type') ?? '') }));
    }
    statics[key]=registry;
    return phpCopy(registry);
}

/** PHP: caster_skill_entry (includes/functions/janus/core/compute/_base.php:684) */
export function casterSkillEntry(arr: unknown, variable: string, env: ComputeBaseEnv): unknown {
    const skills = env.deps.caster_skills(arr), entry = get(skills, variable) ?? null;
    if (isArray(entry)) return env.deps.ft_hydrate_skill_entry(entry, variable);
    if (entry === null && !empty(skills)) {
        const definition = ftSkillRegistryByVar(env).get(variable) ?? null;
        if (definition !== null) {
            const defensive = in_array(get(definition, 'item_type'), list('defensive_skill', 'innate_skill'), true);
            return env.deps.ft_hydrate_skill_entry(record({ tier_level: defensive ? 1 : 0,
                sources: list(defensive ? 'default_defensive' : 'default_flawed') }), variable);
        }
    }
    return null;
}
